import React, { useContext, useEffect, useState } from 'react';
import { AuthContext }    from '../../contexts/AuthContext';
import FlipCard           from '../FlipCard/FlipCard';
import ProfileFront       from './ProfileFront';
import ProfileBack        from './ProfileBack';
import { TooltipContext } from './TooltipContext';
import logo               from '../../assets/images/logo.png';
import { motion }         from 'framer-motion';
import './CRNAProfileCard.css';

const CRNAProfileCard = ({ refreshKey, providerId, profile: providedProfile, readOnly, useProvidedProfile }) => {
  const { auth }            = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [openId, setOpenId]   = useState(null);
  
  // Set the initial profile if useProvidedProfile is true
  useEffect(() => {
    if (useProvidedProfile && providedProfile) {
      console.log('Using provided profile data:', providedProfile);
      setProfile(providedProfile);
    }
  }, [providedProfile, useProvidedProfile]);

  useEffect(() => {
    // Skip API call if we're using the provided profile
    if (useProvidedProfile) return;
    
    let cancelled = false;

    (async () => {
      try {
        // Use the provided providerId if available, otherwise use the authenticated user's ID
        const id = providerId || (auth.user && auth.user.sub);
        
        if (!id || !auth.token) {
          console.warn('Missing provider ID or auth token for profile fetch');
          if (!cancelled) setProfile({});
          return;
        }
        
        const res = await fetch(
          `${process.env.REACT_APP_RXROOSTER_BACKEND_URL}/provider/${id}/profile`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        const { data } = await res.json();
        if (!cancelled) setProfile(data || {});
      } catch (error) {
        console.error('Error fetching provider profile:', error);
        if (!cancelled) setProfile({});
      }
    })();

    return () => { cancelled = true; };
  }, [auth, refreshKey, providerId, useProvidedProfile]);  // ← refetch whenever refreshKey increments or providerId changes

  if (!profile) {
    return (
      <div className="crna-loading">
        <p className="text-muted">Loading profile…</p>
      </div>
    );
  }

  // Enhanced empty state handling
  if (!profile) {
    return (
      <div className="crna-loading">
        <p className="text-muted">Loading profile…</p>
      </div>
    );
  }

  // Check if profile has necessary data to render properly
  const hasMinimalData = profile.profession || profile.certifications || profile.availability;
  if (!hasMinimalData) {
    return (
      <div className="crna-profile-card-wrapper">
        <div className="p-4 text-center">
          <h5>Incomplete Profile</h5>
          <p className="text-muted">This profile doesn't have enough information to display</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipContext.Provider value={{ openId, setOpenId }}>
      <motion.div
        className="crna-profile-card-wrapper"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.05 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        aria-label="CRNA Profile Card"
      >
        <FlipCard
          flippable
          front={<ProfileFront p={profile} />}
          back={<ProfileBack p={profile} />}
          footer={
            <div className="card-footer-logo">
              <img src={logo} alt="RxRooster Logo" width={64} />
            </div>
          }
        />
      </motion.div>
    </TooltipContext.Provider>
  );
};

export default CRNAProfileCard;
